import axios from 'axios';

// Configuración base de la API
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Crear instancia de axios con configuración
const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Interceptor para agregar token JWT automáticamente
apiClient.interceptors.request.use(
  (config) => {
    const user = JSON.parse(localStorage.getItem('clenip_user') || '{}');
    if (user.access_token) {
      config.headers.Authorization = `Bearer ${user.access_token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor para manejar errores de autenticación
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expirado, limpiar localStorage y redirigir a login
      localStorage.removeItem('clenip_user');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

// =============================================================================
// AUTHENTICATION SERVICE
// =============================================================================

export const authService = {
  // Login con backend real
  async login(credentials) {
    try {
      const response = await apiClient.post('/auth/login', credentials);
      const userData = response.data;
      
      // Guardar en localStorage para persistencia
      localStorage.setItem('clenip_user', JSON.stringify(userData));
      
      return userData;
    } catch (error) {
      console.error('Login error:', error);
      throw new Error(error.response?.data?.detail || 'Error de autenticación');
    }
  },

  // Logout
  logout() {
    localStorage.removeItem('clenip_user');
    localStorage.removeItem('clenip_app_data');
  },

  // Obtener usuario actual
  getCurrentUser() {
    try {
      const user = localStorage.getItem('clenip_user');
      return user ? JSON.parse(user) : null;
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  },

  // Verificar si está logueado
  isAuthenticated() {
    const user = this.getCurrentUser();
    return !!(user && user.access_token);
  }
};

// =============================================================================
// ACCOUNTING SERVICE
// =============================================================================

export const accountingService = {
  // Obtener todos los registros contables
  async getRecords() {
    const response = await apiClient.get('/accounting/records');
    return response.data;
  },

  // Crear nuevo registro
  async createRecord(record) {
    const response = await apiClient.post('/accounting/records', record);
    return response.data;
  },

  // Actualizar registro
  async updateRecord(id, record) {
    const response = await apiClient.put(`/accounting/records/${id}`, record);
    return response.data;
  },

  // Eliminar registro
  async deleteRecord(id) {
    const response = await apiClient.delete(`/accounting/records/${id}`);
    return response.data;
  }
};

// =============================================================================
// ORDERS SERVICE
// =============================================================================

export const ordersService = {
  // Obtener órdenes por tipo
  async getOrders(type) {
    const response = await apiClient.get(`/orders/${type}`);
    return response.data;
  },

  // Crear nueva orden
  async createOrder(order) {
    const response = await apiClient.post('/orders', order);
    return response.data;
  },

  // Actualizar orden
  async updateOrder(id, order) {
    const response = await apiClient.put(`/orders/${id}`, order);
    return response.data;
  }
};

// =============================================================================
// TASKS SERVICE
// =============================================================================

export const tasksService = {
  // Obtener todas las tareas
  async getTasks() {
    const response = await apiClient.get('/tasks');
    return response.data;
  },

  // Crear nueva tarea (dispara alarma automática)
  async createTask(task) {
    const response = await apiClient.post('/tasks', task);
    return response.data;
  },

  // Actualizar estado de tarea
  async updateTaskStatus(id, status) {
    const response = await apiClient.put(`/tasks/${id}/status`, { status });
    return response.data;
  },

  // Actualizar calificación de tarea
  async updateTaskRating(id, rating) {
    const response = await apiClient.put(`/tasks/${id}/rating`, { calificacion: rating });
    return response.data;
  }
};

// =============================================================================
// CONFIGURATION SERVICE
// =============================================================================

export const configService = {
  // Métodos de pago
  async getPaymentMethods() {
    const response = await apiClient.get('/config/payment-methods');
    return response.data;
  },

  async createPaymentMethod(name) {
    const response = await apiClient.post('/config/payment-methods', { name });
    return response.data;
  },

  // Áreas
  async getAreas() {
    const response = await apiClient.get('/config/areas');
    return response.data;
  },

  async createArea(name) {
    const response = await apiClient.post('/config/areas', { name });
    return response.data;
  },

  // Cajas
  async getCajas() {
    const response = await apiClient.get('/config/cajas');
    return response.data;
  },

  async createCaja(name) {
    const response = await apiClient.post('/config/cajas', { name });
    return response.data;
  },

  // Colores
  async getColores() {
    const response = await apiClient.get('/config/colores');
    return response.data;
  },

  async createColor(name) {
    const response = await apiClient.post('/config/colores', { name });
    return response.data;
  },

  // DELETE methods
  async deletePaymentMethod(name) {
    const response = await apiClient.delete(`/config/payment-methods/${encodeURIComponent(name)}`);
    return response.data;
  },

  async deleteArea(name) {
    const response = await apiClient.delete(`/config/areas/${encodeURIComponent(name)}`);
    return response.data;
  },

  async deleteCaja(name) {
    const response = await apiClient.delete(`/config/cajas/${encodeURIComponent(name)}`);
    return response.data;
  },

  async deleteColor(name) {
    const response = await apiClient.delete(`/config/colores/${encodeURIComponent(name)}`);
    return response.data;
  },

  async deleteUser(userId) {
    const response = await apiClient.delete(`/users/${userId}`);
    return response.data;
  }
};

// =============================================================================
// USER MANAGEMENT SERVICE
// =============================================================================

export const usersService = {
  // Obtener lista de usuarios
  async getUsers() {
    const response = await apiClient.get('/users');
    return response.data;
  }
};

// =============================================================================
// STATS SERVICE
// =============================================================================

export const statsService = {
  // Obtener estadísticas empresariales
  async getStats() {
    const response = await apiClient.get('/stats');
    return response.data;
  }
};

// =============================================================================
// DATA PERSISTENCE HELPER
// =============================================================================

export const dataService = {
  // Bootstrap: Carga TODOS los datos de MongoDB en un solo request
  async bootstrap() {
    try {
      const response = await apiClient.get('/bootstrap', { timeout: 60000 });
      const d = response.data;

      if (!d.success) throw new Error('Bootstrap failed');

      const data = {
        accounting: d.accounting || [],
        boskoOrders: d.boskoOrders || [],
        luminaOrders: d.luminaOrders || [],
        pendingOrders: d.pendingOrders || [],
        saldos: d.saldos || [],
        materialesPendientes: d.materialesPendientes || [],
        tasks: d.tasks || [],
        scheduledTasks: d.tasks || [],
        paymentMethods: d.paymentMethods || [],
        areas: d.areas || [],
        cajas: d.cajas || [],
        colores: d.colores || [],
        users: (d.users && d.users.length > 0) ? d.users : [
          { id: 1, username: 'admin', password: 'admin123', role: 'admin', name: 'Administrador' },
          { id: 2, username: 'invitado', password: 'guest123', role: 'guest', name: 'Usuario Invitado' }
        ],
        stats: d.stats || {},
        finanzasHistorial: d.finanzasHistorial || [],
        inventoryLocalData: d.inventoryLocalData || {},
        saldosPendientes: d.saldosPendientes || [],
        permissions: {
          admin: ['dashboard', 'accounting', 'bosko', 'lumina', 'orders', 'pending', 'tasks', 'task-scheduler', 'settings'],
          guest: ['dashboard', 'accounting', 'bosko', 'lumina', 'orders', 'pending', 'tasks']
        }
      };

      // Guardar en localStorage como backup
      try {
        const dataStr = JSON.stringify(data);
        if (dataStr.length < 5000000) {
          localStorage.setItem('clenip_app_data', dataStr);
        }
      } catch (e) {
        console.warn('No se pudo guardar backup en localStorage');
      }

      return data;
    } catch (error) {
      console.error('Error en bootstrap, usando fallback individual:', error);
      return this.loadAllData();
    }
  },

  // Fallback: Cargar datos con llamadas individuales
  async loadAllData() {
    try {
      const [
        accounting, boskoOrders, luminaOrders, pendingOrders, saldos,
        materialesPendientes, tasks, paymentMethods, areas, cajas, colores, users, stats
      ] = await Promise.all([
        accountingService.getRecords().catch(() => []),
        ordersService.getOrders('bosko').catch(() => []),
        ordersService.getOrders('lumina').catch(() => []),
        pendingOrdersService.getPendingOrders().catch(() => []),
        saldosService.getSaldos().catch(() => []),
        materialPendienteService.getMateriales().catch(() => []),
        tasksService.getTasks().catch(() => []),
        configService.getPaymentMethods().catch(() => []),
        configService.getAreas().catch(() => []),
        configService.getCajas().catch(() => []),
        configService.getColores().catch(() => []),
        usersService.getUsers().catch(() => []),
        statsService.getStats().catch(() => ({}))
      ]);

      return {
        accounting: accounting || [],
        boskoOrders: boskoOrders || [],
        luminaOrders: luminaOrders || [],
        pendingOrders: pendingOrders || [],
        saldos: saldos || [],
        materialesPendientes: materialesPendientes || [],
        tasks: tasks || [],
        scheduledTasks: tasks || [],
        paymentMethods: paymentMethods || [],
        areas: areas || [],
        cajas: cajas || [],
        colores: colores || [],
        users: (users && users.length > 0) ? users : [
          { id: 1, username: 'admin', password: 'admin123', role: 'admin', name: 'Administrador' },
          { id: 2, username: 'invitado', password: 'guest123', role: 'guest', name: 'Usuario Invitado' }
        ],
        stats: stats || {},
        finanzasHistorial: [],
        inventoryLocalData: {},
        saldosPendientes: [],
        permissions: {
          admin: ['dashboard', 'accounting', 'bosko', 'lumina', 'orders', 'pending', 'tasks', 'task-scheduler', 'settings'],
          guest: ['dashboard', 'accounting', 'bosko', 'lumina', 'orders', 'pending', 'tasks']
        }
      };
    } catch (error) {
      console.error('Error loading data from server:', error);
      try {
        const localData = localStorage.getItem('clenip_app_data');
        return localData ? JSON.parse(localData) : this.getDefaultData();
      } catch (localError) {
        return this.getDefaultData();
      }
    }
  },

  // Datos por defecto
  getDefaultData() {
    return {
      accounting: [], boskoOrders: [], luminaOrders: [], pendingOrders: [],
      tasks: [], scheduledTasks: [], saldos: [], materialesPendientes: [],
      paymentMethods: ['Efectivo', 'Tarjeta', 'Transferencia'],
      areas: ['Administración', 'Ventas', 'Producción', 'Logística'],
      cajas: ['Caja Menor', 'Caja Mayor'], colores: [],
      users: [
        { id: 1, username: 'admin', password: 'admin123', role: 'admin', name: 'Administrador' },
        { id: 2, username: 'invitado', password: 'guest123', role: 'guest', name: 'Usuario Invitado' }
      ],
      finanzasHistorial: [], inventoryLocalData: {}, saldosPendientes: [],
      permissions: {
        admin: ['dashboard', 'accounting', 'bosko', 'lumina', 'orders', 'pending', 'tasks', 'task-scheduler', 'settings'],
        guest: ['dashboard', 'accounting', 'bosko', 'lumina', 'orders', 'pending', 'tasks']
      },
      stats: {}
    };
  }
};

// =============================================================================
// ATTACHMENTS SERVICE
// =============================================================================

export const attachmentsService = {
  // Subir archivo adjunto a un pedido
  async uploadFile(orderId, file) {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await apiClient.post(`/orders/${orderId}/attachments`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Obtener archivos adjuntos de un pedido
  async getOrderAttachments(orderId) {
    const response = await apiClient.get(`/orders/${orderId}/attachments`);
    return response.data;
  },

  // Descargar archivo adjunto
  async downloadFile(fileId, originalName) {
    const response = await apiClient.get(`/attachments/${fileId}/download`, {
      responseType: 'blob',
    });
    
    // Crear URL temporal para descarga
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', originalName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  // Eliminar archivo adjunto
  async deleteFile(fileId) {
    const response = await apiClient.delete(`/attachments/${fileId}`);
    return response.data;
  }
};

// =============================================================================
// PENDING ORDERS SERVICE
// =============================================================================

export const pendingOrdersService = {
  async getPendingOrders() {
    console.log('🔄 Obteniendo pedidos pendientes desde backend...');
    const response = await apiClient.get('/pending-orders');
    console.log('✅ Pedidos pendientes obtenidos:', response.data.length);
    return response.data;
  },

  async createPendingOrder(order) {
    console.log('🔄 Creando pedido pendiente en backend:', order);
    const response = await apiClient.post('/pending-orders', order);
    console.log('✅ Pedido pendiente creado:', response.data);
    return response.data;
  },

  async updatePendingOrder(id, order) {
    console.log('🔄 Actualizando pedido pendiente:', id, order);
    const response = await apiClient.put(`/pending-orders/${id}`, order);
    console.log('✅ Pedido pendiente actualizado:', response.data);
    return response.data;
  },

  async deletePendingOrder(id) {
    console.log('🔄 Eliminando pedido pendiente:', id);
    const response = await apiClient.delete(`/pending-orders/${id}`);
    console.log('✅ Pedido pendiente eliminado');
    return response.data;
  }
};

export const saldosService = {
  async getSaldos() {
    console.log('🔄 Obteniendo saldos desde backend...');
    const response = await apiClient.get('/saldos');
    console.log('✅ Saldos obtenidos:', response.data.length);
    return response.data;
  },

  async createSaldo(saldo) {
    console.log('🔄 Creando saldo en backend:', saldo);
    const response = await apiClient.post('/saldos', saldo);
    console.log('✅ Saldo creado:', response.data);
    return response.data;
  },

  async updateSaldo(id, saldo) {
    console.log('🔄 Actualizando saldo:', id, saldo);
    const response = await apiClient.put(`/saldos/${id}`, saldo);
    console.log('✅ Saldo actualizado:', response.data);
    return response.data;
  },

  async deleteSaldo(id) {
    console.log('🔄 Eliminando saldo:', id);
    const response = await apiClient.delete(`/saldos/${id}`);
    console.log('✅ Saldo eliminado');
    return response.data;
  }
};

export const materialPendienteService = {
  async getMateriales() {
    console.log('🔄 Obteniendo materiales pendientes desde backend...');
    const response = await apiClient.get('/material-pendiente');
    console.log('✅ Materiales pendientes obtenidos:', response.data.length);
    return response.data;
  },

  async createMaterial(material) {
    console.log('🔄 Creando material pendiente en backend:', material);
    const response = await apiClient.post('/material-pendiente', material);
    console.log('✅ Material pendiente creado:', response.data);
    return response.data;
  },

  async updateMaterial(id, material) {
    console.log('🔄 Actualizando material pendiente:', id, material);
    const response = await apiClient.put(`/material-pendiente/${id}`, material);
    console.log('✅ Material pendiente actualizado:', response.data);
    return response.data;
  },

  async deleteMaterial(id) {
    console.log('🔄 Eliminando material pendiente:', id);
    const response = await apiClient.delete(`/material-pendiente/${id}`);
    console.log('✅ Material pendiente eliminado');
    return response.data;
  }
};

export const pedidosCalService = {
  async getAll() {
    const response = await apiClient.get('/pedidos-cal');
    return response.data;
  },
  async save(order) {
    const response = await apiClient.put(`/pedidos-cal/orders/${order.id}`, order);
    return response.data;
  },
  async remove(id) {
    const response = await apiClient.delete(`/pedidos-cal/orders/${id}`);
    return response.data;
  },
  async saveParams(params) {
    const response = await apiClient.put('/pedidos-cal/params', { params });
    return response.data;
  }
};

export default apiClient;